import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Loader2,
  CheckCircle,
  Sparkles,
  Crown,
  Building2,
  Lock,
  ArrowRight,
  Mail,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscriptionPage = () => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    if (!user || !token) {
      navigate('/login');
      return;
    }
    fetchSubscriptionStatus();
  }, [user, token, navigate]);

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscriptionStatus(response.data);
    } catch (e) {
      // ignore; UI gracefully degrades
    } finally {
      setLoadingStatus(false);
    }
  };

  if (loadingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const isCompany = user && ['azienda_manifatturiera', 'azienda_automazione'].includes(user.user_type);
  const isAutomation = user?.user_type === 'azienda_automazione';

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="subscription-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <CheckCircle className="h-4 w-4" />
            {t('sub_current_free')}
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-4 tracking-tight"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {t('sub_page_title')}
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">{t('sub_page_subtitle')}</p>
        </div>

        {/* Current FREE plan card */}
        <Card className="mb-10 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-2xl">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-14 h-14 rounded-xl bg-emerald-500 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2
                    className="text-2xl font-bold text-slate-900"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {t('sub_current_plan_label')}
                  </h2>
                  <Badge className="bg-emerald-500 hover:bg-emerald-500 text-white">{t('sub_active_badge')}</Badge>
                </div>
                <p className="text-slate-600 mb-4">{t('sub_free_desc')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span>{t('sub_perk_1')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span>{t('sub_perk_2')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span>{t('sub_perk_3')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <span>{t('sub_perk_4')}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coming soon: premium plans (only relevant for companies) */}
        {isCompany && (
          <>
            <div className="text-center mb-8">
              <span className="text-xs font-mono uppercase tracking-widest text-orange-600 font-semibold">
                {t('sub_coming_soon_badge')}
              </span>
              <h3
                className="text-2xl md:text-3xl font-bold text-slate-900 mt-3 mb-2"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {t('sub_premium_title')}
              </h3>
              <p className="text-slate-600 text-sm">{t('sub_premium_subtitle')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {/* Premium (Manifatturiera) */}
              <Card className={`border-2 rounded-2xl relative overflow-hidden ${!isAutomation ? 'border-orange-300 bg-gradient-to-br from-white to-orange-50/30' : 'border-slate-200'}`}>
                <div className="absolute top-4 right-4">
                  <Badge variant="outline" className="bg-white text-slate-500 border-slate-300">
                    <Lock className="h-3 w-3 mr-1" />
                    {t('sub_coming_soon_short')}
                  </Badge>
                </div>
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center mb-4">
                    <Crown className="h-6 w-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{t('sub_plan_premium_name')}</h3>
                  <p className="text-sm text-slate-500 mb-4">{t('sub_plan_premium_target')}</p>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-4xl font-bold text-slate-900">€29</span>
                    <span className="text-slate-500 text-sm">{t('sub_per_month')}</span>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t('sub_premium_feat_1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t('sub_premium_feat_2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t('sub_premium_feat_3')}</span>
                    </li>
                  </ul>
                  <Button disabled className="w-full bg-slate-200 text-slate-500 cursor-not-allowed rounded-md py-5">
                    {t('sub_notify_me')}
                  </Button>
                </CardContent>
              </Card>

              {/* Business (Automazione) */}
              <Card className={`border-2 rounded-2xl relative overflow-hidden ${isAutomation ? 'border-orange-300 bg-gradient-to-br from-white to-orange-50/30' : 'border-slate-200'}`}>
                <div className="absolute top-4 right-4">
                  <Badge variant="outline" className="bg-white text-slate-500 border-slate-300">
                    <Lock className="h-3 w-3 mr-1" />
                    {t('sub_coming_soon_short')}
                  </Badge>
                </div>
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center mb-4">
                    <Building2 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{t('sub_plan_business_name')}</h3>
                  <p className="text-sm text-slate-500 mb-4">{t('sub_plan_business_target')}</p>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-4xl font-bold text-slate-900">€39</span>
                    <span className="text-slate-500 text-sm">{t('sub_per_month')}</span>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t('sub_business_feat_1')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t('sub_business_feat_2')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{t('sub_business_feat_3')}</span>
                    </li>
                  </ul>
                  <Button disabled className="w-full bg-slate-200 text-slate-500 cursor-not-allowed rounded-md py-5">
                    {t('sub_notify_me')}
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Contact for early access */}
            <Card className="border border-slate-200 rounded-xl bg-slate-900 text-white">
              <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-orange-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold mb-1">{t('sub_early_access_title')}</h4>
                  <p className="text-sm text-slate-300">{t('sub_early_access_desc')}</p>
                </div>
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap"
                  onClick={() => navigate('/#contact')}
                >
                  {t('sub_early_access_cta')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          </>
        )}

        {/* Back to dashboard */}
        <div className="text-center mt-10">
          <Button
            variant="outline"
            onClick={() => navigate(isCompany ? '/directory' : '/dashboard')}
            className="rounded-md"
            data-testid="back-btn"
          >
            ← {isCompany ? t('nav_directory') : t('nav_dashboard')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
